import { findFirstNonEmptyString, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseImage } from "~/components/common/base-image";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useTrackImpressionEventByIntersection } from "~/components/tracking/google-analytics/hooks";
import type { MoreOnThisWidgetEvents } from "~/components/tracking/google-analytics/types";
import { getAbsoluteUrl } from "~/lib/utils";
import type { articleMoreOnThisListArticle$key } from "~/queries/__generated__/articleMoreOnThisListArticle.graphql";

import type { StyledProps } from "./styles";
import {
  Item,
  StyledContainer,
  StyledEntityLink,
  StyledFigure,
  StyledHeader,
  StyledTitle,
} from "./styles";

export type Props = {
  className?: string;
  reference: articleMoreOnThisListArticle$key;
} & Partial<PropsFromStyledProps<StyledProps>>;

export const ArticleMoreOnThisList: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  variant = "horizontal",
}) => {
  const { moreOnThisArticles } = useFragment(
    graphql`
      fragment articleMoreOnThisListArticle on Article {
        moreOnThisArticles {
          ...entityLink
          entityId
          headline
          socialHeadline
          urlAlias
          images {
            url
            style(filter: { style: "237x147" }) {
              url
            }
          }
        }
      }
    `,
    reference_,
  );

  const articles = moreOnThisArticles?.filter(notEmpty);

  const impressionEvent = useCallback<() => MoreOnThisWidgetEvents>(
    () => ({
      action: "More on this Story/Impression",
      category: "More on this Story",
      label: "",
    }),
    [],
  );
  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection(
    impressionEvent,
    { intersectionThreshold: [0.1], shouldThrottle: true },
  );

  const renderImage = (article: Article) => {
    const mainImage = first(article.images);
    const source = mainImage?.style?.url ?? mainImage?.url;
    return (
      source && (
        <BaseImage
          $aspectRatio="237 / 147"
          $height="150px"
          $objectFit="cover"
          $width="220px"
          alt={article.headline ?? undefined}
          src={source}
          disableLinkingData
        />
      )
    );
  };

  const renderTitle = (article: Article) => (
    <StyledTitle>{findFirstNonEmptyString(article.socialHeadline, article.headline)}</StyledTitle>
  );

  if (!articles?.length) return null;

  return (
    <StyledContainer
      $variant={variant}
      className={className}
      ref={captureTrackImpressionEventTargetElement}
    >
      {articles.map(article => (
        <Item key={article.entityId}>
          <StyledEntityLink
            reference={article}
            onClick={() => {
              sendGATracking({
                action: "More on this Story/Click",
                category: "More on this Story",
                label: `${getAbsoluteUrl(article?.urlAlias)}`,
              });
            }}
          >
            <StyledFigure>
              <StyledHeader>Read more</StyledHeader>
              {renderImage(article)}
              {renderTitle(article)}
            </StyledFigure>
          </StyledEntityLink>
        </Item>
      ))}
    </StyledContainer>
  );

  type Article = Exclude<typeof articles, undefined>[number];
};

ArticleMoreOnThisList.displayName = "ArticleMoreOnThisList";
